<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { reportFilters } from '@/modules/reportTemplates/filters'

/*
* Компонент табличного отображения списка заявок (тикетов)
*
* Основные функции:
* - Отображает список заявок с пагинацией и бесконечной подгрузкой
* - Поддерживает фильтрацию по службам, отделениям, создателям, статусам и датам
* - Предоставляет быстрые действия: создание заявки, сброс фильтров
* - Реализует клик по строке для перехода к деталям заявки
* - Интегрируется с Centrifugo для realtime-обновлений
*
* Особенности:
* - Адаптивный дизайн для мобильных устройств
* - Система чипов для отображения активных фильтров
* - Пресеты дат для быстрой фильтрации по периодам
* - Поддержка темной темы Vuetify
*/

// Импорты Vue и Vue Router
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Импорты Vuetify
import { useTheme } from 'vuetify'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты хранилищ
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useTicketsStore } from '@/modules/tickets/stores/ticketsStore'

// Импорты утилит и сервисов
import { ERouteNames } from '@/router/ERouteNames'
import { formatDateOnly, formatTimeOnly } from '@/utils/formatters'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'
import { useExportUrl } from '@/composables/useExportUrl'

// Импорты типов
import type { ICreateTicketPayload } from '@/modules/tickets/types/ICreateTicketPayload'
import type { ITicketListItem } from '@/modules/tickets/types/ITicketListItem'

// Импорты компонентов

import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import TicketCategoriesModal from './settings/TicketCategoriesModal.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { TICKETS_ANCHORS, TICKETS_CONTEXTS } from '@/modules/onboarding/scenarios/tickets/anchors'
import { TICKETS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/tickets/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

// Импорт Composable
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

// Инициализация утилит отображения Vuetify
const theme = useTheme()
const { smAndDown } = useDisplay()

// Инициализация хранилищ и сервисов
const feedbackStore = useFeedbackStore()
const tickets = useTicketsStore()
const router = useRouter()
const apiStore = useApiStore()
const permissionsStore = usePermissionsStore()
const route = useRoute()

const isCategoriesModalOpen = ref(false)

// Категории из шестерёнки бокового меню: событием на этой странице или ?settings= при переходе с другой; право — как у кнопки «Настройки»
const openSettingsModal = (kind: unknown) => {
  if (kind === 'categories' && permissionsStore.modules.ticket?.category_edit)
    isCategoriesModalOpen.value = true
}

const handleTicketsSettingsEvent = (e: Event) => openSettingsModal((e as CustomEvent<string>).detail)

onMounted(() => {
  window.addEventListener('open-tickets-settings', handleTicketsSettingsEvent)
  openSettingsModal(route.query.settings)
})

watch(() => route.query.settings, openSettingsModal)

onUnmounted(() => {
  window.removeEventListener('open-tickets-settings', handleTicketsSettingsEvent)
})

// Извлекаем Refs для двусторонней синхронизации пагинации и сортировки
const { currentSortString, nextCursor } = storeToRefs(tickets)
const { itemsPerPage } = useTableItemsPerPage('tickets', 25)
const { hideCompleted } = useTableHideCompleted('tickets', true)
tickets.filters.is_actual = hideCompleted.value ? true : null

// Синхронизация с хранилищем тикетов
tickets.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  tickets.itemsPerPage = newVal
})

// Конфигурация полей фильтров
const filterConfigs: IFilterConfig[] = [
  { key: 'services', labelPrefix: 'Служба: ', type: 'tree' },
  { key: 'departments', labelPrefix: 'Отделение: ', type: 'tree' },
  { key: 'categories', labelPrefix: 'Категория: ', type: 'tree' },
  { key: 'numbers', labelPrefix: 'Номер: ', type: 'number' },
  { key: 'coordinators', labelPrefix: 'Координатор: ', type: 'tree' },
  { key: 'creators', labelPrefix: 'Регистратор: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: (val: string) => getStatusLabel(val) },
  { key: 'date_time', labelPrefix: 'Дата: ', type: 'date_range', rangeStartKey: 'created_after', rangeEndKey: 'created_before' },
  { key: 'is_active', labelPrefix: '', type: 'boolean', getCustomLabel: () => 'Мои активные Заявки' },
  { key: 'requires_action', labelPrefix: 'Требует действия: ', type: 'boolean' },
  { key: 'is_actual', labelPrefix: '', type: 'boolean', silent: true },
]

// Инициализация Composable
const tableLogic = useTableFilters({
  filters: computed(() => tickets.filters),
  searchChoices: computed(() => tickets.searchChoices) as any,
  filterChoices: computed(() => tickets.filterChoices) as any,
  filterLoading: tickets.filterLoading,
  fetchFilterChoices: (key: string) => tickets.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => tickets.fetchTickets(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: () => {
    // В ticketsStore нет метода resetFilters, поэтому реализуем его локально; переключатель «Скрыть завершённые» сброс не трогает
    tickets.filters = {
      search: '',
      categories: [],
      coordinators: [],
      priorities: [],
      status: [],
      created_after: null,
      created_before: null,
      is_active: null,
      is_actual: tickets.filters.is_actual,
      services: [],
      departments: [],
      creators: [],
      numbers: [],
    }
  },
  nextCursor,
  loadedItemsCount: computed(() => tickets.tickets.length),
  totalItemsCount: computed(() => tickets.totalTicketsCount),
  sortMap: {
    number: 'number',
    service: 'service',
    category: 'category',
    department: 'department',
    creator: 'created_by',
    priority: 'is_high_priority',
    coordinator: 'coordinator_name',
    date: 'created_at',
  },
  filterConfigs,
})

const isRequiresActionFilterActive = computed(() => tickets.filters.requires_action === true)

const displayedActiveFilterChips = computed(() =>
  tableLogic.activeFilterChips.value.map(chip => {
    if (chip.config?.key === 'requires_action') return { ...chip, label: 'Требует действия' }
    return chip
  }),
)

const toggleRequiresActionFilter = () => {
  tickets.filters.requires_action = isRequiresActionFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

const isHideCompleted = computed({
  get: () => tickets.filters.is_actual === true,
  set: (val: boolean) => {
    tickets.filters.is_actual = val ? true : null
    hideCompleted.value = val
    tableLogic.triggerServerFilter()
  },
})

/**
 * Возвращает читаемое название статуса тикета
 */
function getStatusLabel(status: string) {
  if (!status) {
    return 'Ожидает'
  }
  const statuses = apiStore.responsibilityEntriesStatuses

  return (statuses as Record<string, string>)[status] || status
}

/**
 * Свойства строк таблицы для обработки кликов
 */
const rowProps = (item: { item: ITicketListItem }) => {
  return {
    onClick: () => handleRowClick(item.item),
    class: {
      'row--requires-action': item.item.requires_action === true,
    },
    'data-onboarding': item.item.id === onboardingFormDemoId.value ? TICKETS_ANCHORS.table.demoRow : undefined,
  }
}

/**
 * Обработчик клика по строке таблицы
 */
const handleRowClick = async (item: ITicketListItem) => {
  await router.push({
    name: ERouteNames.TICKET_DETAIL,
    params: { id: item.id },
  })
}

/**
 * Обработчик применения фильтра по номеру
 */
const applyNumberFilterWrapper = () => {
  const inputStr = tableLogic.tempValues.value.numbers || ''
  if (inputStr.trim() === '') {
    tickets.filters.numbers = []
  } else {
    tickets.filters.numbers = inputStr
      .split(/[,;\s]+/)
      .map((num: string) => Number.parseInt(num.trim()))
      .filter((num: number) => !isNaN(num) && num > 0)
  }
  tableLogic.filterMenus.value.numbers = false
  tableLogic.triggerServerFilter()
}

/**
 * Парсит дату
 */
const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) {
    return null
  }
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) {
    return null
  }

  return new Date(year, month - 1, day)
}

/**
 * Обёртка для применения дат
 */
const applyDateFilterWrapper = () => {
  tickets.filters.created_after = parseDateFromInput(tableLogic.tempValues.value.created_after || '')
  tickets.filters.created_before = parseDateFromInput(tableLogic.tempValues.value.created_before || '')
  tableLogic.triggerServerFilter()
  tableLogic.filterMenus.value.date_time = false
}

// Заголовки таблицы заявок с метаданными
const headers = computed(() => [
  { title: '№', key: 'number', maxWidth: '4.5rem', sortable: true, filterType: 'number', filterKey: 'numbers' },
  { title: 'Проблема', key: 'problem', maxWidth: '50rem', sortable: false },
  { title: 'Служба', key: 'service', sortable: true, filterType: 'tree', filterKey: 'services' },
  { title: 'Категория', key: 'category', sortable: true, filterType: 'tree', filterKey: 'categories' },
  { title: 'Отделение', key: 'department', maxWidth: '15rem', sortable: true, filterType: 'tree', filterKey: 'departments' },
  { title: 'Координатор', key: 'coordinator', sortable: true, filterType: 'tree', filterKey: 'coordinators' },
  { title: 'Регистратор', key: 'creator', maxWidth: '6rem', sortable: true, filterType: 'tree', filterKey: 'creators' },
  { title: 'Приоритет', key: 'priority', maxWidth: '10rem', sortable: true },
  { title: 'Дата', key: 'date', sortable: true, filterType: 'date_range', filterKey: 'date_time' },
  { title: 'Время', key: 'time', sortable: false },
  { title: 'Статус', key: 'status', sortable: false, filterType: 'tree', filterKey: 'status' },
] as any[])

const displayedTickets = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return tickets.tickets.slice(start, end)
})

/* Онбординг: демо-заявки первыми в таблице и действия, которыми сценарий открывает меню, модалки и форму */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('ticket', displayedTickets)
const onboardingFormDemoId = computed(() => onboardingStore.activeDemoItems[TICKETS_FORM_DEMO_INDEX]?.list.id ?? null)
const isActionsMenuOpen = ref<number | undefined>(undefined)
const isSettingsMenuOpen = ref(false)

useOnboardingContext({
  names: [TICKETS_CONTEXTS.table],
  scenarioId: 'tickets',
  root: () => document.body,
  actions: {
    openActionsMenu: () => {
      if (smAndDown.value)
        isActionsMenuOpen.value = 0
    },
    openSettingsMenu: () => { isSettingsMenuOpen.value = true },
    closeSettingsMenu: () => { isSettingsMenuOpen.value = false },
    openCategories: () => { isCategoriesModalOpen.value = true },
    closeCategories: () => { isCategoriesModalOpen.value = false },
    openWorkGroups: async () => { await router.push({ name: ERouteNames.TICKETS_WORK_GROUPS }) },
    openDemoForm: async () => {
      if (onboardingFormDemoId.value !== null)
        await router.push({ name: ERouteNames.TICKET_DETAIL, params: { id: onboardingFormDemoId.value }, query: { [ONBOARDING_DEMO_QUERY]: '1' } })
    },
    closeForm: async () => { await router.push({ name: ERouteNames.TICKETS }) },
  },
})

/**
 * Обработчик создания нового тикета
 */
async function createTicket() {
  tickets.currentTicket = {
    description: '',
    is_high_priority: false,
    location: null,
    location_str: '',
    department: null,
    category: null,
    service: null,
  } as ICreateTicketPayload
  await router.push({ name: ERouteNames.CREATE_TICKET })
}

const goToTicketsStats = async () => {
  await router.push({ name: ERouteNames.TICKETS_STATS })
}

const { download: downloadExport, isExporting } = useExportUrl(
  '/api/ticket/ticket/export/',
  () => tickets.filters,
)

/*
 * Хук монтирования компонента.
 *
 * Подписка на realtime больше здесь не поднимается: общий диспетчер
 * в src/utils/subscribeHelpers.ts слушает единый канал user:user#sub
 * и маршрутизирует ticket_* в ticketsStore.applyTicketRealtime.
 */
onMounted(async () => {
  await tickets.fetchTickets()

  // Автостарт только на самой таблице: при прямом заходе в карточку заявки обучение не перебивает работу
  if (router.currentRoute.value.name === ERouteNames.TICKETS)
    void onboardingStore.autoStart('tickets')
})
const templateFilters = computed(() => reportFilters(tickets.filters))
</script>

<template>
  <header :class="{ 'd-flex flex-column gap-2 mb-4': smAndDown } ">
    <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
      <VBtn
        :class="{ 'w-100': smAndDown }"
        variant="elevated"
        :loading="feedbackStore.isGlobalLoading"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="TICKETS_ANCHORS.table.create"
        @click="createTicket"
      >
        Создать заявку
      </VBtn>

      <!-- На мобильных скрываем кнопки навигации/действий в Expansion Panel -->
      <VExpansionPanels v-if="smAndDown" v-model="isActionsMenuOpen" class="w-100 mt-1">
        <VExpansionPanel>
          <VExpansionPanelTitle class="d-flex align-center py-2 px-4" style="min-height: 48px;">
            <div class="d-flex align-center gap-2">
              <VIcon icon="ri-menu-2-line" size="20" />
              <span class="text-subtitle-1 font-weight-medium">Меню действий</span>
            </div>
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <div class="d-flex flex-column gap-2 w-100">
              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-pie-chart-line"
                :data-onboarding="TICKETS_ANCHORS.table.statistics"
                @click="router.push({ name: ERouteNames.TICKETS_GRAPH })"
              >
                Статистика
              </VBtn>

              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-line-chart-line"
                :data-onboarding="TICKETS_ANCHORS.table.reports"
                @click="goToTicketsStats"
              >
                Отчеты
              </VBtn>

              <!-- Меню настроек тикетов (мобильная версия) -->
              <VMenu
                v-if="permissionsStore.modules.ticket?.category_edit || permissionsStore.modules.ticket?.work_group_edit"
                v-model="isSettingsMenuOpen"
                location="bottom end"
                offset="5px"
              >
                <template #activator="{ props: menuProps }">
                  <VBtn
                    v-bind="menuProps"
                    class="w-100"
                    variant="outlined"
                    color="secondary"
                    prepend-icon="ri-settings-4-line"
                    :data-onboarding="TICKETS_ANCHORS.table.settings"
                  >
                    Настройки
                  </VBtn>
                </template>
                <VList min-width="220" :data-onboarding="TICKETS_ANCHORS.table.settingsCategories">
                  <VListItem
                    v-if="permissionsStore.modules.ticket?.category_edit"
                    title="Категории заявок"
                    @click="isCategoriesModalOpen = true"
                  />
                  <VListItem
                    v-if="permissionsStore.modules.ticket?.work_group_edit"
                    title="Группы исполнителей"
                    @click="router.push({ name: ERouteNames.TICKETS_WORK_GROUPS })"
                  />
                </VList>
              </VMenu>

              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-share-2-line"
                :loading="isExporting"
                :data-onboarding="TICKETS_ANCHORS.table.export"
                @click="downloadExport"
              >
                Выгрузить
              </VBtn>
              <div class="d-flex justify-end gap-2">
                <ReportTemplateMenu object-type="ticket" context="collection" :filters="templateFilters" :sort="tickets.currentSortString" variant="toolbar" />
                <OnboardingLaunchButton scenario="tickets" />
              </div>
            </div>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <!-- На ПК показываем обычный горизонтальный ряд кнопок -->
      <div
        v-else
        class="d-flex align-center gap-2"
      >
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-pie-chart-line"
          :data-onboarding="TICKETS_ANCHORS.table.statistics"
          @click="router.push({ name: ERouteNames.TICKETS_GRAPH })"
        >
          Статистика
        </VBtn>

        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-line-chart-line"
          :data-onboarding="TICKETS_ANCHORS.table.reports"
          @click="goToTicketsStats"
        >
          Отчеты
        </VBtn>

        <!-- Меню настроек тикетов (десктоп версия) -->
        <VMenu
          v-if="permissionsStore.modules.ticket?.category_edit || permissionsStore.modules.ticket?.work_group_edit"
          v-model="isSettingsMenuOpen"
          location="bottom end"
          offset="5px"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              variant="outlined"
              color="secondary"
              class="bg-surface"
              :class="{ 'w-100': smAndDown }"
              prepend-icon="ri-settings-4-line"
              :data-onboarding="TICKETS_ANCHORS.table.settings"
            >
              Настройки
            </VBtn>
          </template>
          <VList min-width="220" :data-onboarding="TICKETS_ANCHORS.table.settingsCategories">
            <VListItem
              v-if="permissionsStore.modules.ticket?.category_edit"
              title="Категории заявок"
              @click="isCategoriesModalOpen = true"
            />
            <VListItem
              v-if="permissionsStore.modules.ticket?.work_group_edit"
              title="Группы исполнителей"
              @click="router.push({ name: ERouteNames.TICKETS_WORK_GROUPS })"
            />
          </VList>
        </VMenu>

        <VIconBtn
          color="secondary"
          class="rounded-sm"
          icon="ri-share-2-line"
          :icon-size="20"
          variant="outlined"
          :rounded="false"
          :size="38"
          :loading="isExporting"
          :data-onboarding="TICKETS_ANCHORS.table.export"
          @click="downloadExport"
        />
        <ReportTemplateMenu object-type="ticket" context="collection" :filters="templateFilters" :sort="tickets.currentSortString" variant="toolbar" />
        <OnboardingLaunchButton scenario="tickets" />
      </div>
    </div>
  </header>

  <div class="wrapper">
    <div>
      <div
        class="d-flex align-center gap-4"
        :class="[
          tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
          { 'flex-column': smAndDown },
        ]"
      >
        <div
          class="d-flex align-center gap-2 w-100"
          :class="{ 'flex-column': smAndDown }"
        >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0"
            :class="smAndDown ? 'w-100' : 'w-25'"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
            :data-onboarding="TICKETS_ANCHORS.table.search"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="tableLogic.applySearchFilter()"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            :class="{ 'w-100': smAndDown }"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="tableLogic.applySearchFilter()"
          />

          <VBtn
            :class="[
              { 'w-100': smAndDown },
              !smAndDown && 'ml-auto',
            ]"
            :prepend-icon="tableLogic.getFilterIcon('requires_action')"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            :data-onboarding="TICKETS_ANCHORS.table.requiresAction"
            @click="toggleRequiresActionFilter"
          >
            Требует действия
          </VBtn>

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            :class="{ 'w-100': smAndDown }"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            :data-onboarding="TICKETS_ANCHORS.table.resetFilters"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>

          <VSwitch
            v-model="isHideCompleted"
            :data-onboarding="TICKETS_ANCHORS.table.hideCompleted"
            label="Скрыть завершенные"
            density="compact"
            color="primary"
            hide-details
            class="flex-grow-0"
          />
        </div>
      </div>

      <div
        v-if="displayedActiveFilterChips.length"
        class="filter-chip d-flex ml-4 align-center gap-2 py-3"
      >
        <VChip
          v-for="chip in displayedActiveFilterChips"
          :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <VDataTableServer
        :items="tableRows"
        :items-length="tickets.totalTicketsCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div
            class="header-content d-flex align-center"
            :data-onboarding="TICKETS_ANCHORS.table.filterPrefix + col.key"
          >
            <span>{{ column.title }}</span>

            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <!-- Спиннер -->
            <div
              v-if="tableLogic.filterLoading?.[col.filterKey!]"
              class="d-flex align-center justify-center ml-1"
              style="width: 28px; height: 28px"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <!-- Иконки/Меню -->
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <!-- Number Filter -->
            <VMenu
              v-if="col.filterType === 'number'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="pa-4">
                <VTextField
                  v-model="tableLogic.tempValues.value[col.filterKey!]"
                  autofocus
                  placeholder="Например: 123, 456, 789"
                  hide-details
                  width="220px"
                  @keyup.enter="applyNumberFilterWrapper"
                />
                <div class="d-flex justify-end mt-2">
                  <VBtn
                    :class="{ 'w-100': smAndDown }"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    @click="applyNumberFilterWrapper"
                  >
                    Применить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>

            <!-- Date Filter -->
            <VMenu
              v-else-if="col.filterType === 'date_range'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <DateRangeFilterCard
                :date-after="tableLogic.tempValues.value.created_after || ''"
                :date-before="tableLogic.tempValues.value.created_before || ''"
                @update:date-after="tableLogic.tempValues.value.created_after = $event"
                @update:date-before="tableLogic.tempValues.value.created_before = $event"
                @apply="applyDateFilterWrapper"
              />
            </VMenu>

            <!-- Tree Filter -->
            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="tickets"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>
        <!-- КОНЕЦ ДИНАМИЧЕСКИХ ЗАГОЛОВКОВ -->

        <!-- Статические ячейки заголовков -->
        <template #[`header.problem`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`header.priority`]="{ column }">
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon('priority')"
              :class="{
                'is-active': tableLogic.isSortActive('priority'),
                'is-desc': tableLogic.isSortDesc('priority'),
              }"
            />
          </div>
        </template>

        <template #[`header.time`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <!-- Ячейки данных -->
        <template #[`item.number`]="{ item }">
          <span>{{ item.number }}</span>
        </template>

        <template #[`item.problem`]="{ item }">
          <CollapsibleText :text="item.description" />
        </template>

        <template #[`item.service`]="{ item }">
          <span>{{ item.service?.short_name || item.service?.name || 'Служба не указана' }}</span>
        </template>

        <template #[`item.category`]="{ item }">
          <span class="cell-content">{{ item.category_name }}</span>
        </template>

        <template #[`item.department`]="{ item }">
          <span>{{ item.department?.short_name || item.department?.name || 'Отделение не указано' }}</span>
        </template>

        <template #[`item.coordinator`]="{ item }">
          <ShortFio
            :person="item.coordinator"
            empty-text="Не назначен"
          />
        </template>

        <template #[`item.creator`]="{ item }">
          <ShortFio :person="item.created_by" />
        </template>

        <template #[`item.priority`]="{ item }">
          <div
            v-if="item.is_high_priority"
            class="d-flex align-center gap-2"
          >
            <span class="risk-dot high" />
            <span>Высокий</span>
          </div>
        </template>

        <template #[`item.date`]="{ item }">
          <span>{{ formatDateOnly(item.created_at) }}</span>
        </template>

        <template #[`item.time`]="{ item }">
          <span>{{ formatTimeOnly(item.created_at) }}</span>
        </template>

        <template #[`item.status`]="{ item }">
          <VChip
            class="status-badge"
            :class="item.status"
            size="small"
          >
            {{ getStatusLabel(item.status) }}
          </VChip>
        </template>

        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>
  <RouterView :key="String($route.params.id ?? $route.name)" />

  <TicketCategoriesModal v-model="isCategoriesModalOpen" />
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr.row--requires-action > .v-data-table__td:first-child) {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-wrapper {
  min-height: 4.125rem;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

/* Стили для priority-dot */
.priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.priority-dot.low {
  background-color: var(--p-green-400);
}

.priority-dot.medium {
  background-color: var(--p-orange-400);
}

.priority-dot.high {
  background-color: var(--p-yellow-400);
}

.priority-dot.critical {
  background-color: var(--p-red-400);
}

.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background-color: #f44336;
}

/* Адаптивные стили */
.cursor-pointer {
  cursor: pointer;
}

.text-italic {
  font-style: italic;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gap-2 {
  gap: 8px;
}

:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

/* Минимальные ширины колонок для лучшего отображения */
:deep(.v-data-table__th:nth-child(1)) { min-width: 6rem !important; }
:deep(.v-data-table__th:nth-child(2)) { min-width: 4rem !important; }
:deep(.v-data-table__th:nth-child(3)) { min-width: 6.25rem !important; }
:deep(.v-data-table__th:nth-child(4)) { min-width: 8.125rem !important; }
:deep(.v-data-table__th:nth-child(5)) { min-width: 10rem !important; }
:deep(.v-data-table__th:nth-child(6)) { min-width: 10rem !important; }
:deep(.v-data-table__th:nth-child(7)) { min-width: 10rem !important; }
:deep(.v-data-table__th:nth-child(8)) { min-width: 9rem !important; }
:deep(.v-data-table__th:nth-child(9)) { min-width: 4.375rem !important; }
:deep(.v-data-table__th:nth-child(10)) { min-width: 3.75rem !important; }
:deep(.v-data-table__th:nth-child(11)) { min-width: 8rem !important; }

:deep(.v-data-table__th) {
  white-space: nowrap;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 16px 16px 16px !important;
}
</style>
